import React, { useCallback, useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminAuth } from "../context/SuperAdminAuthContext";
import saApi, { SA_API } from "../lib/saApi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DatabaseBackup, Download, Trash2, Loader2, HardDrive, Copy, RefreshCw, AlertTriangle, Database, Clock,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmtBytes = (n) => {
  const b = Number(n || 0);
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  const u = ["KB", "MB", "GB", "TB"];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
};
const num = (n) => Number(n || 0).toLocaleString();
const fmtTs = (v) => (v ? new Date(v).toLocaleString() : "—");

const Tile = ({ label, value, sub, testid }) => (
  <div className="rounded-2xl border border-white/10 p-4 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
    <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
    <div data-testid={testid} className="text-xl font-semibold text-white mt-1">{value}</div>
    {sub && <div className="text-[11px] text-white/40 mt-0.5">{sub}</div>}
  </div>
);

const STATUS = {
  ok: ["Ready", "bg-emerald-500/15 text-emerald-300 border-emerald-400/20"],
  running: ["Running", "bg-cyan-500/15 text-cyan-300 border-cyan-400/20"],
  failed: ["Failed", "bg-red-500/15 text-red-300 border-red-400/20"],
  pruned: ["File removed", "bg-white/10 text-white/50 border-white/10"],
};

export default function SABackups() {
  const { sa } = useSuperAdminAuth();
  const isMaster = !!sa?.is_master;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [downloading, setDownloading] = useState("");

  const load = useCallback(() => {
    saApi.get("/backups").then(({ data }) => setData(data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load backups"); setData({ items: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const backupNow = async () => {
    setBusy(true);
    try {
      const { data: row } = await saApi.post("/backups", { note: note.trim() || null });
      toast.success(`Backup ready — ${row.file} (${fmtBytes(row.size_bytes)})`);
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally { setBusy(false); }
  };

  const download = async (b) => {
    setDownloading(b.id);
    try {
      const res = await saApi.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/gzip" }));
      const a = document.createElement("a");
      a.href = url; a.download = b.file; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      // Blob error bodies need decoding before we can show the reason.
      let msg = "Download failed";
      try { msg = JSON.parse(await e?.response?.data?.text())?.detail || msg; } catch { /* keep default */ }
      toast.error(msg);
      load();
    } finally { setDownloading(""); }
  };

  const doDelete = async () => {
    const b = confirmDelete;
    setConfirmDelete(null);
    try { await saApi.delete(`/backups/${b.id}`); toast.success("Backup deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const copyHint = (b) => { navigator.clipboard.writeText(b.restore_hint || ""); toast.success("Restore command copied"); };

  if (!data) {
    return <SuperAdminShell title="Database Backup">
      <div className="px-5 md:px-8 py-6 text-white/50 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
    </SuperAdminShell>;
  }

  const dbs = data.database || {};
  const items = data.items || [];

  return (
    <SuperAdminShell
      title="Database Backup"
      actions={
        <div className="flex items-center gap-2">
          <Input data-testid="sa-backup-note" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Label this backup (optional)"
            className="h-9 w-[220px] bg-white/5 border-white/10 text-white placeholder:text-white/30 text-xs" />
          <Button data-testid="sa-backup-now" onClick={backupNow} disabled={busy || !isMaster}
            title={!isMaster ? "Master super admin required" : "Write a full database dump now"}
            className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <DatabaseBackup className="w-4 h-4 mr-1.5" />}
            {busy ? "Backing up…" : "Back up now"}
          </Button>
        </div>
      }
    >
      <div className="px-5 md:px-8 py-6 space-y-5" data-testid="sa-backups-page">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile label="Database" value={dbs.name || "—"} sub={`${num(dbs.collections)} collections`} testid="sa-backup-db-name" />
          <Tile label="Live data" value={fmtBytes(dbs.data_bytes)} sub={`${num(dbs.documents)} documents`} testid="sa-backup-db-size" />
          <Tile label="Backups on this server" value={num(items.filter((i) => i.status === "ok").length)}
            sub={`keeping the newest ${data.keep}`} testid="sa-backup-count" />
          <Tile label="Free disk" value={fmtBytes(data.free_disk_bytes)} sub={`via ${data.method}`} testid="sa-backup-free-disk" />
        </div>

        <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 flex items-start gap-2.5" data-testid="sa-backup-warning">
          <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-100/80 leading-relaxed">
            A backup is written to this server&apos;s own disk (<code className="text-amber-200">{data.backup_dir}</code>),
            so it lives only until the container restarts or the newest {data.keep} are exceeded. Download the file to keep
            it. Every backup and download is recorded in the audit log.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
          <table className="w-full text-sm" data-testid="sa-backups-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5">
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3">Taken</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Schema</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!items.length ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40" data-testid="sa-backups-empty">
                  No backups yet — take one with “Back up now”. One is also taken automatically before every database upgrade.
                </td></tr>
              ) : items.map((b) => {
                const [label, cls] = STATUS[b.status] || STATUS.pruned;
                return (
                  <tr key={b.id} data-testid={`sa-backup-row-${b.id}`} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-2.5">
                      <div className="text-white/90 text-xs font-mono truncate max-w-[260px]">{b.file}</div>
                      {b.note && <div className="text-[10px] text-white/40 truncate max-w-[260px]">{b.note}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-white/60 whitespace-nowrap">
                      <span className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-white/30" />{fmtTs(b.created_at)}</span>
                      <span className="text-[10px] text-white/35">{b.created_by}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-white/70 whitespace-nowrap">
                      {fmtBytes(b.size_bytes)}
                      {b.duration_ms ? <span className="text-[10px] text-white/35"> · {(b.duration_ms / 1000).toFixed(1)}s</span> : null}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-white/60">{b.schema_version ? `v${b.schema_version}` : "—"}</td>
                    <td className="px-4 py-2.5 text-[11px] text-white/50">{b.reason}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={`text-[10px] ${cls}`}>{label}</Badge>
                      {b.error && <div className="text-[10px] text-red-300/80 max-w-[200px] truncate" title={b.error}>{b.error}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5 justify-end">
                        {b.status === "ok" && (
                          <>
                            <button data-testid={`sa-backup-download-${b.id}`} onClick={() => download(b)}
                              disabled={!isMaster || downloading === b.id} title={isMaster ? "Download" : "Master super admin required"}
                              className="text-cyan-300 hover:text-white disabled:text-white/20">
                              {downloading === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            </button>
                            <button data-testid={`sa-backup-hint-${b.id}`} onClick={() => copyHint(b)} title="Copy the restore command"
                              className="text-white/50 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                        <button data-testid={`sa-backup-delete-${b.id}`} onClick={() => setConfirmDelete(b)} disabled={!isMaster}
                          title={isMaster ? "Delete" : "Master super admin required"}
                          className="text-white/40 hover:text-red-300 disabled:text-white/15"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-white/40 max-w-3xl">
            Restore a downloaded archive with{" "}
            <code className="text-white/60">mongorestore --uri=&quot;$MONGO_URL&quot; --gzip --archive=&lt;file&gt; --drop</code>
            {" "}— the copy icon on each row gives you the exact command for that file. Direct link for scripted pulls:{" "}
            <code className="text-white/60">{SA_API}/backups/&lt;id&gt;/download</code>
          </p>
          <Button data-testid="sa-backups-refresh" size="sm" variant="outline" onClick={load}
            className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
          </Button>
        </div>

        {!!(dbs.heaviest || []).length && (
          <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
            <div className="px-4 py-2.5 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5 flex items-center gap-2">
              <Database className="w-3.5 h-3.5" />What takes up the space
            </div>
            <div className="divide-y divide-white/5" data-testid="sa-backup-heaviest">
              {dbs.heaviest.map((c) => (
                <div key={c.name} className="px-4 py-2 flex items-center gap-3 text-xs">
                  <span className="text-white/80 flex-1 truncate font-mono text-[11px]">{c.name}</span>
                  <span className="text-white/40">{num(c.documents)} docs</span>
                  <span className="text-white/70 w-20 text-right">{fmtBytes(c.bytes)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isMaster && (
          <p className="text-[11px] text-amber-300/80 flex items-center gap-1.5">
            <HardDrive className="w-3.5 h-3.5" />Only a master super admin can take, download or delete a backup.
          </p>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="sa-backup-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this backup?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.file} will be removed from this server. If you have not downloaded it, it is gone for good.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="sa-backup-delete-yes" onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SuperAdminShell>
  );
}
